import { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import ProductCardNew from "../components/ProductCardNew";
import { useProducts } from "../hooks/useProducts";
import { useCanvaAuth } from "../contexts/CanvaAuthContext2";
import { initiateCheckout } from "../handlers/paymentHandler";
import {
  getMonthlyDisplayPrice,
  getAnnualDisplayPrice,
  shouldShowRecurringToggle,
  isFreeProduct,
  isCustomProduct,
} from "../utils/pricingViewUtils";

const currencies = [
  { code: "USD", flag: "🇺🇸" },
  { code: "EUR", flag: "🇪🇺" },
  { code: "YEN", flag: "🇯🇵" },
  { code: "MXN", flag: "🇲🇽" },
  { code: "PLN", flag: "🇵🇱" },
];

const PricingViewNew = () => {
  const { canvaAuthenticated, token } = useCanvaAuth();
  const { sortedByTier } = useProducts();

  // Get current user's product ID from their plan
  const currentUserProductId = canvaAuthenticated?.meResponse?.userProfile?.plan?.productId || null;

  // Calculate available currencies from pricing data
  const availableCurrencies = useMemo(() => {
    const currencySet = new Set<string>();

    // Collect all unique currencies from active pricing options
    sortedByTier.forEach((productItem) => {
      productItem.product.pricing.forEach((pricing) => {
        if (pricing.is_active) {
          currencySet.add(pricing.currency);
        }
      });
    });

    // Filter the currencies array to only include available currencies
    return currencies.filter((currency) => currencySet.has(currency.code));
  }, [sortedByTier]);

  // Get the default currency (prefer USD if available, otherwise use first available)
  const defaultCurrency = useMemo(() => {
    const usdAvailable = availableCurrencies.some((c) => c.code === "USD");
    return usdAvailable ? "USD" : (availableCurrencies[0]?.code || "USD");
  }, [availableCurrencies]);

  const [recurringOptions, setRecurringOptions] = useState<Record<number, "recurring" | "onetime">>({});
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [selectedCurrency, setSelectedCurrency] = useState(defaultCurrency);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [descriptionHeight, setDescriptionHeight] = useState<number | null>(null);
  const [featuresHeight, setFeaturesHeight] = useState<number | null>(null);
  const [loadingProductId, setLoadingProductId] = useState<number | null>(null);

  const descriptionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const featuresRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize recurring options for all products
  useEffect(() => {
    if (sortedByTier.length > 0) {
      const initialOptions = sortedByTier.reduce((acc, item) => {
        return { ...acc, [item.product.id]: "recurring" };
      }, {});
      setRecurringOptions(initialOptions);
    }
  }, [sortedByTier]);

  // Update selected currency if default changes and current selection is invalid
  useEffect(() => {
    if (!availableCurrencies.some((c) => c.code === selectedCurrency)) {
      setSelectedCurrency(defaultCurrency);
    }
  }, [defaultCurrency, availableCurrencies, selectedCurrency]);

  const toggleRecurring = (productId: number) => {
    setRecurringOptions((prev) => ({
      ...prev,
      [productId]: prev[productId] === "recurring" ? "onetime" : "recurring",
    }));
  };

  const handleSelectPlan = async (productId: number) => {
    if (!token) {
      console.error("No authentication token available");
      return;
    }

    // Set loading state for this product
    setLoadingProductId(productId);

    try {
      console.log("Initiating checkout for product:", productId);

      // Construct the checkout request
      const checkoutRequest = {
        products: [{ id: productId }],
        success_url: `${window.location.origin}/success`,
        cancel_url: `${window.location.origin}/canva-back`,
      };

      // Initiate the checkout session
      const checkoutResponse = await initiateCheckout(token, checkoutRequest);

      // Redirect to the checkout URL
      if (checkoutResponse.checkout_url) {
        window.location.href = checkoutResponse.checkout_url;
      } else {
        console.error("No checkout URL received");
        setLoadingProductId(null);
      }
    } catch (error) {
      console.error("Failed to initiate checkout:", error);
      setLoadingProductId(null);
      // You might want to show an error message to the user here
    }
  };

  // Calculate max heights for descriptions and features
  useEffect(() => {
    const calculateHeights = () => {
      setDescriptionHeight(null);
      setFeaturesHeight(null);

      setTimeout(() => {
        const descHeights = descriptionRefs.current
          .filter(ref => ref !== null)
          .map(ref => ref!.scrollHeight);
        const maxDescHeight = descHeights.length > 0 ? Math.max(...descHeights) : null;

        const featHeights = featuresRefs.current
          .filter(ref => ref !== null)
          .map(ref => ref!.scrollHeight);
        const maxFeatHeight = featHeights.length > 0 ? Math.max(...featHeights) : null;

        setDescriptionHeight(maxDescHeight);
        setFeaturesHeight(maxFeatHeight);
      }, 50);
    };

    calculateHeights();
    window.addEventListener('resize', calculateHeights);

    return () => {
      window.removeEventListener('resize', calculateHeights);
    };
  }, [sortedByTier]);

  // Helper function to determine if product should be promoted
  const isProductPromoted = (productId: number): boolean => {
    // You can customize this logic based on your business rules
    // For now, let's promote tier 3 products
    const product = sortedByTier.find(p => p.product.id === productId);
    return product ? product.product.tier === 3 : false;
  };

  // Helper function to get product badge
  const getProductBadge = (productId: number): string | null => {
    const productItem = sortedByTier.find(p => p.product.id === productId);
    if (!productItem) return null;

    const product = productItem.product;

    if (product.tier <= 1.0) return "Free";
    if (product.tier === 3) return "Popular";
    if (product.tier >= 100) return "Enterprise";
    return null;
  };

  // Mock features - you might want to derive these from the product or fetch separately
  const getProductFeatures = (productId: number): { description: string }[] => {
    const product = sortedByTier.find(p => p.product.id === productId);
    if (!product) return [];

    // This is a placeholder - you should define features based on your product structure
    // You might want to add a features field to the ProductDetails interface
    const tier = product.product.tier;

    if (tier <= 1.0) {
      return [
        { description: "15 minutes of music generation" },
        { description: "Basic features" },
      ];
    } else if (tier === 2) {
      return [
        { description: "115 minutes of music generation" },
        { description: "Commercial & private use" },
        { description: "30 tracks per day" },
      ];
    } else if (tier === 3) {
      return [
        { description: "4 hours of music generation" },
        { description: "Commercial & private use" },
        { description: "50 tracks per day" },
        { description: "Text/tags to music" },
        { description: "Super-long 10min tracks" },
      ];
    } else {
      return [
        { description: "Unlimited music generation" },
        { description: "Priority support" },
        { description: "Custom integrations" },
      ];
    }
  };

  if (sortedByTier.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-slate-900 mb-2">Loading products...</h2>
          <p className="text-slate-600">Please wait while we load available plans</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      {/* Desktop Layout */}
      <div className="hidden lg:block pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Currency Selector */}
          <div className="flex justify-end mb-2">
            <div className="relative">
              <button
                onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg bg-white hover:bg-slate-50 transition-colors"
              >
                <span>
                  {availableCurrencies.find((c) => c.code === selectedCurrency)?.flag}
                </span>
                <span>{selectedCurrency}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showCurrencyDropdown && (
                <div className="absolute right-0 mt-2 w-32 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                  {availableCurrencies.map((currency) => (
                    <button
                      key={currency.code}
                      onClick={() => {
                        setSelectedCurrency(currency.code);
                        setShowCurrencyDropdown(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 hover:bg-slate-50 transition-colors"
                    >
                      <span>{currency.flag}</span>
                      <span>{currency.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Header */}
          <div className="mb-6 text-center">
            <h1 className="text-slate-900 mb-2 text-5xl">
              Choose Your Plan
            </h1>
            <p className="text-slate-600 text-xl">
              Powered by AI Music Technology
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center gap-0 mb-8 bg-slate-100 rounded-full p-1 w-fit mx-auto">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`px-6 py-2 rounded-full transition-all ${
                billingPeriod === "monthly"
                  ? "bg-white shadow-sm text-slate-900"
                  : "text-slate-600"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod("annual")}
              className={`px-6 py-2 rounded-full transition-all flex items-center gap-2 ${
                billingPeriod === "annual"
                  ? "bg-purple-600 text-white"
                  : "text-slate-600"
              }`}
            >
              Annual
            </button>
          </div>

          {/* Pricing Cards */}
          <div className="overflow-x-auto -mx-8 px-8">
            <div className="flex items-stretch gap-6 pb-12 pt-16 min-w-max pr-8">
              {sortedByTier.map((productItem, index) => (
                <ProductCardNew
                  key={productItem.product.id}
                  product={productItem.product}
                  selectedCurrency={selectedCurrency}
                  billingPeriod={billingPeriod}
                  currentPlanProductId={currentUserProductId}
                  onSelectPlan={handleSelectPlan}
                  descriptionRef={(el) => (descriptionRefs.current[index] = el)}
                  featuresRef={(el) => (featuresRefs.current[index] = el)}
                  descriptionHeight={descriptionHeight}
                  featuresHeight={featuresHeight}
                  recurringOption={recurringOptions[productItem.product.id] || "recurring"}
                  onToggleRecurring={() => toggleRecurring(productItem.product.id)}
                  isPromoted={isProductPromoted(productItem.product.id)}
                  badge={getProductBadge(productItem.product.id)}
                  features={getProductFeatures(productItem.product.id)}
                  isLoading={loadingProductId === productItem.product.id}
                  isAnyLoading={loadingProductId !== null}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden h-screen flex flex-col">
        <div className="flex-1 overflow-y-auto px-4 pt-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-slate-900 mb-2 text-2xl">
              Choose Your Plan
            </h1>
            <p className="text-slate-600 text-sm">
              Powered by AI Music Technology
            </p>
          </div>

          {/* Controls */}
          <div className="sticky top-0 z-20 bg-gradient-to-br from-purple-50 to-white pb-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
              <div className="flex flex-col gap-1.5">
                {/* Monthly/Annual */}
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-slate-600 whitespace-nowrap w-[68px]">
                    Billing period
                  </span>
                  <div className="flex items-center gap-0 bg-slate-100 rounded-full p-0.5 flex-1">
                    <button
                      onClick={() => setBillingPeriod("monthly")}
                      className={`px-2 py-0.5 rounded-full transition-all text-[10px] flex-1 ${
                        billingPeriod === "monthly"
                          ? "bg-purple-600 text-white"
                          : "text-slate-600"
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBillingPeriod("annual")}
                      className={`px-2 py-0.5 rounded-full transition-all text-[10px] flex-1 ${
                        billingPeriod === "annual"
                          ? "bg-purple-600 text-white"
                          : "text-slate-600"
                      }`}
                    >
                      Annual
                    </button>
                  </div>
                </div>

                {/* Currency */}
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-slate-600 whitespace-nowrap w-[68px]">
                    Currency
                  </span>
                  <div className="relative flex-1">
                    <button
                      onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-0.5 bg-slate-100 rounded-full text-[10px]"
                    >
                      <span className="flex items-center gap-1">
                        <span>{availableCurrencies.find((c) => c.code === selectedCurrency)?.flag}</span>
                        <span>{selectedCurrency}</span>
                      </span>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    {showCurrencyDropdown && (
                      <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                        {availableCurrencies.map((currency) => (
                          <button
                            key={currency.code}
                            onClick={() => {
                              setSelectedCurrency(currency.code);
                              setShowCurrencyDropdown(false);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors text-xs"
                          >
                            <span>{currency.flag}</span>
                            <span>{currency.code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Cards - Mobile */}
          <div className="space-y-4 mt-4 pb-8">
            {sortedByTier.map((productItem) => {
              const product = productItem.product;
              const isFree = isFreeProduct(product);
              const isCustom = isCustomProduct(product);
              const isCurrentPlan = product.id === currentUserProductId;
              const features = getProductFeatures(product.id);

              const getCurrencySymbol = () => {
                const symbols = {
                  USD: "$",
                  EUR: "€",
                  PLN: "zł",
                  YEN: "¥",
                  MXN: "$"
                };
                return symbols[selectedCurrency as keyof typeof symbols] || selectedCurrency;
              };

              const formatPrice = (price: number | null) => {
                if (price === null) return "0.00";
                return selectedCurrency === "YEN" ? Math.round(price).toString() : price.toFixed(2);
              };

              const getMonthlyPrice = () => {
                if (isFree || isCustom) return null;
                return getMonthlyDisplayPrice(
                  product,
                  selectedCurrency,
                  billingPeriod,
                  recurringOptions[product.id] || "recurring"
                );
              };

              const getAnnualPrice = () => {
                if (isFree || isCustom) return null;
                return getAnnualDisplayPrice(
                  product,
                  selectedCurrency,
                  recurringOptions[product.id] || "recurring"
                );
              };

              const getPrice = () => {
                if (isFree) return "Free";
                if (isCustom) return "Custom";
                const monthlyPrice = getMonthlyPrice();
                return monthlyPrice ? `${getCurrencySymbol()}${formatPrice(monthlyPrice)}` : "N/A";
              };

              const isPromoted = isProductPromoted(product.id);
              const badge = getProductBadge(product.id);
              const showToggle = shouldShowRecurringToggle(product, selectedCurrency);
              const isLoading = loadingProductId === product.id;
              const isAnyLoading = loadingProductId !== null;

              return (
                <div
                  key={product.id}
                  onClick={() => !isCustom && !isAnyLoading && handleSelectPlan(product.id)}
                  className={`bg-white rounded-xl p-4 shadow-sm transition-all border ${
                    isCurrentPlan
                      ? "border-slate-300"
                      : isPromoted
                      ? "border-purple-500"
                      : "border-slate-200 hover:shadow-md"
                  } ${!isCustom && !isCurrentPlan && !isAnyLoading ? "cursor-pointer" : ""}`}
                >
                  {/* Header */}
                  <div className="mb-3 pb-3 border-b border-slate-200">
                    <div className="flex flex-row items-center justify-between gap-2">
                      <div className="flex flex-col items-start gap-1">
                        {isCurrentPlan && (
                          <div className="px-2 py-0.5 bg-slate-100 rounded-full text-[9px] text-slate-600">
                            Current Plan
                          </div>
                        )}
                        {isPromoted && !isCurrentPlan && (
                          <div className="px-2 py-0.5 bg-purple-100 rounded-full text-[9px] text-purple-700">
                            {badge || "Popular"}
                          </div>
                        )}
                        <h3 className="text-slate-900">{product.name}</h3>
                      </div>
                      <div className="flex flex-col items-end">
                        <div className="text-slate-900">{getPrice()}</div>
                        {!isFree && !isCustom && (
                          <div className="text-[10px] text-slate-500">
                            / month
                          </div>
                        )}
                        {billingPeriod === "annual" && !isFree && !isCustom && getAnnualPrice() && (
                          <div className="text-[9px] text-slate-400">
                            {getCurrencySymbol()}{formatPrice(getAnnualPrice())} / year
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 mb-4 p-0 m-0 list-none">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[10px] text-slate-700">
                        <span className="text-purple-600">✓</span>
                        <span>{feature.description}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    className={`w-full py-2 rounded-lg text-xs transition-all mb-3 flex items-center justify-center gap-2 ${
                      isCurrentPlan
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : isCustom
                        ? "bg-slate-100 text-slate-700 cursor-default"
                        : isAnyLoading
                        ? "bg-purple-400 text-white cursor-not-allowed"
                        : "bg-purple-600 text-white hover:bg-purple-700"
                    }`}
                    disabled={isCurrentPlan || isCustom || isAnyLoading}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      "Current Plan"
                    ) : (
                      "Select Plan"
                    )}
                  </button>

                  {/* Payment Type Switch - always reserve space */}
                  <div
                    className="flex items-center justify-center gap-2 h-[24px]"
                    style={{ opacity: showToggle ? 1.0 : 0 }}
                  >
                    <span
                      className={`text-[10px] ${recurringOptions[product.id] === "onetime" ? "text-slate-900" : "text-slate-500"}`}
                    >
                      One-time
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRecurring(product.id);
                      }}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        recurringOptions[product.id] === "recurring"
                          ? "bg-purple-600"
                          : "bg-slate-300"
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                          recurringOptions[product.id] === "recurring"
                            ? "translate-x-5"
                            : "translate-x-0.5"
                        }`}
                      />
                    </button>
                    <span
                      className={`text-[10px] ${recurringOptions[product.id] === "recurring" ? "text-slate-900" : "text-slate-500"}`}
                    >
                      Recurring
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingViewNew;
